import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2, Play } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { reportError } from "@/components/ErrorBoundary";
import { extractApiErrorMessage } from "@/lib/api-error";
import { errorTitle } from "@/lib/messages";
import {
  LIMITE_VIDEO_BYTES,
  avisarEnviado,
  duracaoDoArquivo,
  formatarDuracao,
  lerEstadoDoVideo,
  passaDoLimite,
  pedirEnvio,
  subirArquivo,
  tipoDoVideo,
  type EstadoDoVideo,
} from "@/lib/videoDoProduto";

export type EnvioDeVideo = {
  estado: EstadoDoVideo;
  // % do PUT, enquanto o navegador envia.
  progresso: number | null;
  subindo: boolean;
};

// O vídeo novo é enviado e acompanhado aqui, fora do formulário: o «Salvar Produto» só liga o id ao produto.
// Fechar o editor cancela o envio e para a consulta (a conversão continua no servidor).
export function useEnvioDeVideo() {
  const { toast } = useToast();
  const [envios, setEnvios] = useState<Record<string, EnvioDeVideo>>({});
  const cancelamentos = useRef(new Map<string, () => void>());
  const relogios = useRef(new Map<string, number>());
  // Muda a cada limpeza: resposta que chega depois de o editor fechar não mexe em nada.
  const geracao = useRef(0);

  const mudar = useCallback((id: string, f: (e: EnvioDeVideo) => EnvioDeVideo) => {
    setEnvios((prev) => (prev[id] ? { ...prev, [id]: f(prev[id]!) } : prev));
  }, []);

  // «Inserindo» leva de 30 s a 1 min: consulta a cada 3 s, por até 10 min.
  const acompanhar = useCallback((id: string) => {
    const g = geracao.current;
    let voltas = 0;
    const agenda = () => {
      if (g !== geracao.current || ++voltas > 200) return;
      relogios.current.set(id, window.setTimeout(passo, 3000));
    };
    const passo = async () => {
      relogios.current.delete(id);
      if (g !== geracao.current) return;
      try {
        const e = await lerEstadoDoVideo(id);
        if (g !== geracao.current || !e) return;
        mudar(id, (atual) => ({ ...atual, estado: e }));
        if (e.status === "inserindo" || e.status === "enviando") agenda();
      } catch {
        agenda();
      }
    };
    agenda();
  }, [mudar]);

  const registrar = useCallback((e: EstadoDoVideo) => {
    setEnvios((prev) => ({ ...prev, [e.id]: { estado: e, progresso: null, subindo: false } }));
    if (e.status === "inserindo") acompanhar(e.id);
  }, [acompanhar]);

  const pararUm = (id: string) => {
    cancelamentos.current.get(id)?.();
    cancelamentos.current.delete(id);
    const t = relogios.current.get(id);
    if (t) window.clearTimeout(t);
    relogios.current.delete(id);
  };

  const cancelar = useCallback((id: string) => {
    pararUm(id);
    setEnvios((prev) => {
      if (!prev[id]) return prev;
      const { [id]: _, ...resto } = prev;
      return resto;
    });
  }, []);

  const limpar = useCallback(() => {
    geracao.current++;
    for (const id of [...cancelamentos.current.keys(), ...relogios.current.keys()]) pararUm(id);
    setEnvios({});
  }, []);

  useEffect(() => () => {
    geracao.current++;
    for (const id of [...cancelamentos.current.keys(), ...relogios.current.keys()]) pararUm(id);
  }, []);

  /** Confere o arquivo, pede o envio e começa o PUT. Devolve o id do vídeo, ou null se recusou (com o aviso). */
  const comecar = useCallback(async (arquivo: File): Promise<string | null> => {
    if (arquivo.size === 0) {
      toast({ title: "Vídeo vazio", description: `"${arquivo.name}" está com 0 bytes. Verifique se o arquivo baixou e tente de novo.`, variant: "destructive" });
      return null;
    }
    if (arquivo.size > LIMITE_VIDEO_BYTES) {
      toast({ title: "Vídeo grande demais", description: `O vídeo tem ${Math.round(arquivo.size / 1024 / 1024)} MB e o limite é 300 MB.`, variant: "destructive" });
      return null;
    }
    const duracao = await duracaoDoArquivo(arquivo);
    if (passaDoLimite(duracao)) {
      toast({ title: "Vídeo longo demais", description: `O vídeo tem ${formatarDuracao(duracao)} e o limite é 30 segundos. Corte e envie de novo.`, variant: "destructive" });
      return null;
    }
    const tipo = tipoDoVideo(arquivo);
    let pedido;
    try {
      pedido = await pedirEnvio(arquivo.size, tipo);
    } catch (err: any) {
      const msg = extractApiErrorMessage(err, "Não consegui começar o envio do vídeo. Tente de novo.");
      reportError(new Error(`[video-envio] ${msg} | status=${err?.status ?? "?"} type=${tipo} size=${arquivo.size}`), null);
      toast({ title: errorTitle("enviar o vídeo"), description: msg, variant: "destructive" });
      return null;
    }
    const id = pedido.id;
    const g = geracao.current;
    setEnvios((prev) => ({
      ...prev,
      [id]: {
        estado: { id, status: "enviando", motivo: null, duracao, capas: null, capa: 0, posicao: 0, webm: null, mp4: null, anterior: null },
        progresso: 0,
        subindo: true,
      },
    }));
    const subida = subirArquivo(pedido, arquivo, (pct) => { if (g === geracao.current) mudar(id, (e) => ({ ...e, progresso: pct })); });
    cancelamentos.current.set(id, subida.cancelar);
    void (async () => {
      try {
        await subida.pronto;
      } catch (err) {
        cancelamentos.current.delete(id);
        if (g !== geracao.current || (err instanceof Error && err.message === "cancelado")) return;
        reportError(new Error(`[video-upload] ${err instanceof Error ? err.message : String(err)} | id=${id} type=${tipo} size=${arquivo.size}`), null);
        mudar(id, (e) => ({ ...e, progresso: null, subindo: false, estado: { ...e.estado, status: "falhou", motivo: "O envio do vídeo não terminou. Confira a internet e envie de novo." } }));
        return;
      }
      cancelamentos.current.delete(id);
      if (g !== geracao.current) return;
      try {
        const e = await avisarEnviado(id);
        if (g !== geracao.current) return;
        mudar(id, (atual) => ({ ...atual, progresso: 100, subindo: false, estado: e ?? { ...atual.estado, status: "inserindo" } }));
        if (!e || e.status === "inserindo") acompanhar(id);
      } catch (err: any) {
        if (g !== geracao.current) return;
        const msg = extractApiErrorMessage(err, "O vídeo não chegou. Envie de novo.");
        reportError(new Error(`[video-enviado] ${msg} | status=${err?.status ?? "?"} id=${id}`), null);
        mudar(id, (atual) => ({ ...atual, progresso: null, subindo: false, estado: { ...atual.estado, status: "falhou", motivo: msg } }));
      }
    })();
    return id;
  }, [toast, mudar, acompanhar]);

  return { envios, comecar, registrar, cancelar, limpar };
}

/** O quadrinho do vídeo (no quadrado do editor e na lista das fotos): a capa escolhida, ou o estado enquanto não fica pronto. */
export function MiniaturaDoVideo({ envio, capa, selo = "canto" }: { envio: EnvioDeVideo | undefined; capa: number; selo?: "canto" | "centro" }) {
  const e = envio?.estado;
  const url = e?.status === "pronto" && e.capas ? (e.capas[capa] ?? e.capas[0]) : null;
  return (
    <div className="absolute inset-0 bg-neutral-800 flex items-center justify-center" data-testid="video-miniatura">
      {url ? (
        <img src={url} alt="Vídeo" className="w-full h-full object-cover pointer-events-none" />
      ) : e?.status === "falhou" ? (
        <AlertTriangle className="w-6 h-6 text-amber-300" />
      ) : (
        <Loader2 className="w-6 h-6 text-white/80 animate-spin" />
      )}
      {selo === "canto" ? (
        <span className="absolute bottom-1 right-1 inline-flex items-center gap-0.5 text-[10px] font-semibold bg-black/65 text-white px-1.5 py-0.5 rounded pointer-events-none">
          <Play className="w-2.5 h-2.5 fill-current" />
          {e?.duracao ? formatarDuracao(e.duracao) : "vídeo"}
        </span>
      ) : url ? (
        <span className="absolute inset-0 m-auto w-8 h-8 rounded-full bg-black/55 text-white flex items-center justify-center pointer-events-none">
          <Play className="w-4 h-4 fill-current ml-0.5" />
        </span>
      ) : null}
    </div>
  );
}

/** Abaixo das fotos, enquanto o produto tem vídeo: o envio, «Inserindo Vídeo», «Vídeo pronto» ou o motivo da falha. A
 *  escolha da capa saiu (Pedro, 28/10/2026): no cardápio o vídeo toca sozinho; quem quer capa põe uma foto antes dele. */
export function PainelDoVideo({
  envio,
  onRemover,
}: {
  envio: EnvioDeVideo | undefined;
  onRemover: () => void;
}) {
  const e = envio?.estado;
  if (!e) return null;
  return (
    <div className="mt-3 rounded-xl border bg-card p-3 space-y-2.5" data-testid="video-painel" data-status={e.status}>
      {e.status === "enviando" && (
        <>
          <div className="flex items-center gap-2 text-sm font-medium">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            <span>Enviando o vídeo… {envio?.progresso ?? 0}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${envio?.progresso ?? 0}%` }} />
          </div>
        </>
      )}
      {e.status === "inserindo" && (
        <>
          <div className="flex items-center gap-2 text-sm font-medium">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            <span>Inserindo Vídeo</span>
          </div>
          <p className="text-xs text-muted-foreground">Pode salvar e sair. O vídeo entra no cardápio quando ficar pronto.</p>
        </>
      )}
      {e.status === "pronto" && (
        <p className="text-sm font-medium">Vídeo pronto{e.duracao ? ` · ${formatarDuracao(e.duracao)}` : ""}</p>
      )}
      {e.status === "falhou" && (
        <p className="text-sm text-destructive" data-testid="video-motivo">{e.motivo || "Não consegui inserir o vídeo. Tente de novo."}</p>
      )}
      {e.anterior && e.status !== "pronto" && (
        <p className="text-xs text-muted-foreground">
          {e.status === "falhou" ? "O cardápio continua com o vídeo anterior." : "Até este ficar pronto, o cardápio segue com o vídeo anterior."}
        </p>
      )}
      <button type="button" onClick={onRemover} className="text-xs font-medium text-destructive hover:underline" data-testid="video-remover">
        Remover vídeo
      </button>
    </div>
  );
}
